/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import 'regenerator-runtime/runtime';
import { configureStore, EnhancedStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import createSagaMiddleware from 'redux-saga';

import { AppDispatch, RootState } from '../ts/state';
import reducer from './reducer';
import saga from './saga';

const sagaMiddleware = createSagaMiddleware();

// See https://redux-toolkit.js.org/usage/usage-guide#simplifying-store-setup-with-configurestore
const store: EnhancedStore = configureStore({
  reducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false
    }).concat(sagaMiddleware)
});

sagaMiddleware.run(saga);

/**
 * Returns the existing store
 * @returns  The single instance of store used across the app
 */
export const getStore = (): EnhancedStore => store;

/**
 * Creates a new store using @param preloadedState as initial state
 * @returns  The store instance
 */
export const createStore = (preloadedState = {}): EnhancedStore =>
  configureStore({
    reducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(sagaMiddleware)
  });

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
